import React, { useState, useEffect } from 'react';
import { Clock, MapPin, ChevronRight, Play, CheckCircle2, Circle, Navigation } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { routesApi } from '../services/api';

const RoutesPage = () => {
  const { darkMode } = useTheme();
  const [routes, setRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [loading, setLoading] = useState(true);

  // Dark mode classes
  const bgMain = darkMode ? 'bg-slate-900' : 'bg-[#f5f7fa]';
  const bgCard = darkMode ? 'bg-slate-800' : 'bg-white';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-slate-400' : 'text-gray-500';

  useEffect(() => {
    const fetchRoutes = async () => {
      try {
        const data = await routesApi.getAll();
        setRoutes(data);
      } catch (error) {
        console.error('Error fetching routes:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchRoutes();
  }, []);

  // Mock route details
  const routeStops = {
    'route-1': [
      { name: 'Giresun Kalesi', duration: '45 dk', completed: false },
      { name: 'Giresun Müzesi', duration: '30 dk', completed: false },
      { name: 'Zeytinlik Mahallesi', duration: '1 saat', completed: false },
      { name: 'Eski Çarşı', duration: '30 dk', completed: false },
      { name: 'Sahil Yürüyüşü', duration: '15 dk', completed: false },
    ],
    'route-2': [
      { name: 'Kuzalan Şelalesi', duration: '1.5 saat', completed: false },
      { name: 'Mavi Göl', duration: '1 saat', completed: false },
      { name: 'Bektaş Yaylası', duration: '1.5 saat', completed: false },
      { name: 'Kümbet Yaylası', duration: '1 saat', completed: false },
    ],
    'route-3': [
      { name: 'Fındık Lokantası', duration: '45 dk', completed: false },
      { name: 'Karadeniz Pide Salonu', duration: '30 dk', completed: false },
      { name: 'Bal Evi', duration: '20 dk', completed: false },
      { name: 'Yöresel Kahvaltıcı', duration: '45 dk', completed: false },
      { name: 'Fındık Ezmesi Dükkanı', duration: '15 dk', completed: false },
      { name: 'Deniz Mahsulleri', duration: '30 dk', completed: false },
    ],
    'route-4': [
      { name: 'Sahil Parkı', duration: '30 dk', completed: false },
      { name: 'Giresun Kalesi Seyir Terası', duration: '45 dk', completed: false },
      { name: 'Ada Manzara Noktası', duration: '1 saat', completed: false },
      { name: 'Gün Batımı Terası', duration: '30 dk', completed: false },
      { name: 'Gece Manzarası', duration: '45 dk', completed: false },
      { name: 'Liman Bölgesi', duration: '30 dk', completed: false },
      { name: 'Eski Şehir Sokakları', duration: '45 dk', completed: false },
    ],
  };

  if (selectedRoute) {
    const stops = routeStops[selectedRoute.id] || [];
    return (
      <div className={`h-full ${bgMain} overflow-auto pb-20`}>
        {/* Route Header */}
        <div className="relative h-80">
          <img
            src={selectedRoute.image}
            alt={selectedRoute.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          <button
            onClick={() => setSelectedRoute(null)}
            className={`absolute top-4 left-4 w-10 h-10 ${darkMode ? 'bg-slate-800/90' : 'bg-white/90'} rounded-full flex items-center justify-center`}
          >
            <ChevronRight className={`w-5 h-5 ${textPrimary} rotate-180`} />
          </button>
          <div className="absolute bottom-4 left-4 right-4 text-white">
            <h1 className="text-2xl font-bold mb-2">{selectedRoute.name}</h1>
            <div className="flex items-center gap-4 text-sm">
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {selectedRoute.duration}
              </span>
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {selectedRoute.places_count} durak
              </span>
            </div>
          </div>
        </div>

        {/* Route Description */}
        <div className="p-4">
          <p className={`${textSecondary} mb-6`}>{selectedRoute.description}</p>

          {/* Start Button */}
          <button className="w-full py-3 bg-[#1e3a5f] text-white rounded-xl font-medium flex items-center justify-center gap-2 hover:bg-[#2a4a73] transition-colors mb-6">
            <Play className="w-5 h-5" />
            Rotayı Başlat
          </button>

          {/* Route Stops */}
          <h2 className={`text-lg font-bold ${textPrimary} mb-4`}>Rota Durakları</h2>
          <div className="space-y-4">
            {stops.map((stop, index) => (
              <div key={index} className="flex items-start gap-4">
                <div className="flex flex-col items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    stop.completed ? 'bg-green-500 text-white' : (darkMode ? 'bg-slate-700 text-slate-400' : 'bg-gray-200 text-gray-500')
                  }`}>
                    {stop.completed ? (
                      <CheckCircle2 className="w-5 h-5" />
                    ) : (
                      <span className="text-sm font-medium">{index + 1}</span>
                    )}
                  </div>
                  {index < stops.length - 1 && (
                    <div className={`w-0.5 h-12 ${darkMode ? 'bg-slate-700' : 'bg-gray-200'} my-1`} />
                  )}
                </div>
                <div className={`flex-1 ${bgCard} rounded-xl p-4 shadow-sm`}>
                  <h3 className={`font-semibold ${textPrimary}`}>{stop.name}</h3>
                  <p className={`text-sm ${textSecondary}`}>Tahmini süre: {stop.duration}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`h-full ${bgMain} overflow-auto pb-20`}>
      <div className="max-w-3xl mx-auto p-4">
        <h1 className={`text-2xl font-bold ${textPrimary} mb-2`}>Gezi Rotaları</h1>
        <p className={`${textSecondary} mb-6`}>Giresun'u keşfetmek için hazır rotalar</p>

        {/* Routes List */}
        <div className="space-y-4">
          {routes.map((route) => (
            <div
              key={route.id}
              onClick={() => setSelectedRoute(route)}
              className={`${bgCard} rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer`}
            >
              <div className="relative h-40">
                <img
                  src={route.image}
                  alt={route.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-3 left-3 right-3 text-white">
                  <h3 className="font-bold text-lg mb-1">{route.name}</h3>
                  <div className="flex items-center gap-3 text-sm">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {route.duration}
                    </span>
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {route.places_count} durak
                    </span>
                  </div>
                </div>
                {route.popular && (
                  <div className="absolute top-3 right-3 px-2 py-1 bg-yellow-400 text-yellow-900 text-xs font-medium rounded-full">
                    Popüler
                  </div>
                )}
              </div>
              <div className="p-4">
                <p className={`text-sm ${textSecondary} line-clamp-2`}>{route.description}</p>
                <button className="mt-3 text-[#1e3a5f] font-medium text-sm flex items-center gap-1 hover:underline">
                  Rotayı İncele
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RoutesPage;
